import { useContext, useEffect, useMemo, useState } from 'react';

import { ReactRelayContext } from 'react-relay';

import type { MentionAgentSource } from '../../services/mention-agents/types.ts';

import { createMentionAgentSource } from './create-mention-agent-source.ts';

export type UseMentionAgentSourceOptions = {
	enabled: boolean;
	cloudId: string | undefined;
	product: string;
	workspaceId?: string;
};

export type UseMentionAgentSourceResult = {
	agentSource: MentionAgentSource | null;
	hasAvailableAgents: boolean;
};

/**
 * Returns a `MentionAgentSource` bound to the current Relay environment, or
 * `null` when the feature is disabled or no Relay environment is mounted.
 *
 * Reads the environment via `ReactRelayContext` (not `useRelayEnvironment()`)
 * so the hook can be safely called on hosts that gate the agent-mention
 * feature off and don't mount a `RelayEnvironmentProvider` (e.g. the
 * Confluence AIFC onboarding surface). `useRelayEnvironment()` throws an
 * invariant in that case and would crash the host.
 */
export const useMentionAgentSource = (
	options: UseMentionAgentSourceOptions,
): UseMentionAgentSourceResult => {
	const environment = useContext(ReactRelayContext)?.environment ?? null;
	const [hasAvailableAgents, setHasAvailableAgents] = useState(false);

	const source = useMemo<MentionAgentSource | null>(() => {
		if (!options.enabled || !environment) {
			return null;
		}
		return createMentionAgentSource({
			environment,
			cloudId: options.cloudId,
			product: options.product,
			workspaceId: options.workspaceId,
		});
	}, [environment, options.enabled, options.cloudId, options.product, options.workspaceId]);

	useEffect(() => {
		if (!source) {
			setHasAvailableAgents(false);
			return;
		}

		const abortController = new AbortController();
		void source
			.getAllAgents(abortController.signal)
			.then((agents) => {
				if (!abortController.signal.aborted) {
					setHasAvailableAgents(agents.length > 0);
				}
			})
			.catch(() => {
				if (!abortController.signal.aborted) {
					setHasAvailableAgents(false);
				}
			});
		return () => abortController.abort();
	}, [source]);

	return {
		agentSource: source,
		hasAvailableAgents,
	};
};
